import { Injectable } from '@angular/core';
import {
    FilterModel,
    FilterType,
    PagingModel,
    SortingDirection,
    SortingModel,
} from 'lemunzo-api';
import { FilterMetadata, SortMeta } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { FilterMatchModeToFilterType } from '../models/filter-paginator-sort-model';
import { ConstantsService } from './constants.service';
import { TableConfigService } from './table-config.service';

/**
 * Request body shape accepted by the generated `*ListRequestModel` list
 * endpoints (paging + optional sortings/filters). Mirrors the backend's generic
 * `ListRequestModel<T>` (Apcam.Api.Core.App.Features.Pagination.Models).
 *
 * Declared locally and entity-agnostic on purpose: the OpenAPI generator
 * flattens the BE generic into one identical interface per entity
 * (`EvidenceDocumentModelListRequestModel`, `ClaimModelListRequestModel`, …),
 * so there is no single generated type to reuse. Because TypeScript is
 * structural, this shape is assignable to every one of those per-entity types.
 */
interface TableListRequestModel {
    paging: PagingModel;
    sortings?: SortingModel[] | null;
    filters?: FilterModel[] | null;
}

@Injectable({
    providedIn: 'root',
})
export class QueryService {
    public static handleTrailingSlash(url: string): string {
        return url + (url.endsWith('/') ? '' : '/');
    }

    /**
     * Capitalizes the first letter of a string.
     *
     * @param str The input string.
     * @returns The string with the first letter capitalized.
     */
    public static capitalizeString(str: string): string {
        return str[0].toUpperCase() + str.slice(1);
    }

    /**
     * A simple helper for creating table query parameters.
     *
     * @param ids IDs of the entities to filter by, concatenated via OR operator
     * @param filters other filters to apply
     * @param skipValue paging skip value, default 0
     * @param takeValue paging take value, default MAX_PAGING_ITEMS
     * @returns a list request model for generated API list endpoints
     */
    public static getListRequestModel(
        ids: number[] = [],
        filters: FilterModel[] = [],
        skipValue: number = 0,
        takeValue: number = ConstantsService.MAX_PAGING_ITEMS
    ): TableListRequestModel {
        const paging: PagingModel = {
            skip: skipValue,
            take: takeValue,
        };

        // helper for an easy ID filter creation
        const idFilters: FilterModel[] = ids.map((id) => ({
            key: 'Id',
            value: id.toString(),
            type: FilterType.Equals,
        }));

        return { paging: paging, filters: [...filters, ...idFilters] };
    }

    /**
     * Builds a typed list request model for generated API list endpoints
     * (JSON-serialized query param via OpenAPI generator).
     *
     * @param event PrimeNG lazy-load event carrying the user's paging, sorting
     * and interactive column filters.
     *
     * @param baseFilters Filters applied to every request regardless of the
     * lazy-load event — server-side scoping constraints the user cannot see or
     * change. They are merged ahead of the user's column filters. Typical use is
     * a row-ownership scope such as `ClientProfileId`, ensuring a client only
     * ever receives their own records.
     *
     * @param caseIds Optional admin multi-case scope. Each ID is emitted as a
     * separate filter entry under the same key so the backend combines them with
     * OR (`case A OR case B`). Filters with different keys are combined with AND
     * (see `QueryableExtensions.ApplyFilters`). This is why case IDs are handled
     * separately from `baseFilters`.
     *
     * @returns Typed request model with paging, sortings and merged filters.
     */
    public static getTableListRequestModel(
        event: TableLazyLoadEvent,
        baseFilters: FilterModel[] = [],
        caseIds: number[] = []
    ): TableListRequestModel {
        const paging: PagingModel = {
            skip: event.first ?? 0,
            take: event.rows ?? TableConfigService.TableRowDefault,
        };

        const sortings = this.buildListSortings(event.multiSortMeta);
        const tableFilters = this.buildListFilters(event.filters, caseIds);
        const filters =
            baseFilters.length > 0 || tableFilters.length > 0
                ? [...baseFilters, ...tableFilters]
                : null;

        return {
            paging,
            sortings: sortings.length > 0 ? sortings : null,
            filters,
        };
    }

    private static buildListSortings(
        multiSort: SortMeta[] | null | undefined
    ): SortingModel[] {
        if (!multiSort) {
            return [];
        }

        return multiSort.map((sort) => ({
            key: this.capitalizeString(sort.field),
            direction: sort.order as SortingDirection,
        }));
    }

    /**
     * Converts PrimeNG column filters into the flat {@link FilterModel} list
     * consumed by the generated list endpoints. Each active filter maps its
     * match mode to a {@link FilterType} (falling back to `Equals`), trims string
     * values, and skips empty ones (but keeps `0`). For `caseIds` OR-semantics
     * see {@link getTableListRequestModel}.
     *
     * @param filters PrimeNG lazy-load `event.filters` (single metadata per key).
     * @param caseIds Admin multi-case scope: when non-empty, each id is emitted
     * as a separate filter entry under the `CaseId` column key (OR-ed by the backend),
     * alongside the user's column filter value. Client evidence passes `[]`.
     * @returns Merged filter models, in column order with case ids emitted first.
     */
    private static buildListFilters(
        filters:
            | {
                  [s: string]: FilterMetadata | FilterMetadata[] | undefined;
              }
            | undefined,
        caseIds: number[]
    ): FilterModel[] {
        const result: FilterModel[] = [];

        caseIds.forEach((caseId: number) => {
            result.push({
                key: 'CaseId',
                value: caseId.toString(),
                type: FilterType.Equals,
            });
        });

        if (!filters) {
            return result;
        }

        Object.keys(filters).forEach((key) => {
            const filterMetadata = filters[key] as FilterMetadata;

            if (!filterMetadata) {
                return;
            }

            const filterMode = filterMetadata.matchMode;
            let filterValue = filterMetadata.value;
            if (typeof filterValue === 'string') {
                filterValue = filterValue.trim();
            }

            if (filterValue != null && filterValue !== '' && filterMode) {
                const filterType =
                    FilterMatchModeToFilterType.get(filterMode) ??
                    FilterType.Equals;

                result.push({
                    key: this.capitalizeString(key),
                    value: String(filterValue),
                    type: filterType,
                });
            }
        });

        return result;
    }
}
